import type { ItemFieldRow } from './item-field-reader';
import { readItemField } from './item-field-reader';
import { readInventoryField } from './inventory-raw-readers';
import { storedStatusRows, readStatusCasterId } from './creature-status-reader';
import { fromBaseRecord, fromBaseUuid, FromBaseInputMissing, FromBaseStageMissing } from './from-base-context';
import type { FromBaseRow } from './from-base-context';
import { statusItemId, STATUS_IDS } from '../statuses/expiry';
import { phpJsonInt } from './php-json-values';
import { adoptCreatureVehicleSpeeds } from './creature-vehicle-speed-adoption';
import { MOUNT_RESTRICTED_SPEEDS } from './creature-mount-speed-restriction';
import type { CreatureVehicleSpeedAdoptionInput } from '../types';
import { findInventoryRow } from '../inventory/flag-adjust';
import { normalizePhpListish } from '../../shared/php-listish';
type MutableSlots<T> = { -readonly [Key in keyof T]: T[Key] };

const upper = (value: unknown): string => String(value ?? '').toUpperCase();
export class PilotedObjectUnavailable extends Error {}

export function pilotedStatusPeers(rows: readonly FromBaseRow[]): FromBaseRow[] {
    return rows.flatMap(row => {
        const base = fromBaseRecord(row._entity_base) && fromBaseRecord(row._entity_base.base) ? row._entity_base.base : null;
        return [...storedStatusRows(row, 'target'), ...storedStatusRows(row, 'caster'),
            ...Array.isArray(base?.status_connection_rows) ? base.status_connection_rows.filter(fromBaseRecord) : []];
    });
}

/** The counterpart's TARGET is the vehicle. Neither the caster nor the connection is a vehicle id.
 * Raw bases already hold the pilot's outgoing statuses; action frames supply their current peers. */
export function resolvePilotedObjectId(host: ItemFieldRow, peers: readonly ItemFieldRow[] = []): string | null {
    if (!fromBaseRecord(host)) return null;
    const self = upper(readItemField(fromBaseRecord(host.encounter_creatures) ? host.encounter_creatures : host, 'encounter_creature_id')
        ?? readInventoryField(host, 'inventory_id') ?? readItemField(host, 'item_id'));
    const base = fromBaseRecord(host._entity_base) && fromBaseRecord(host._entity_base.base) ? host._entity_base.base : null;
    const outgoing = [...storedStatusRows(host, 'caster'), ...peers,
        ...Array.isArray(base?.status_connection_rows) ? base.status_connection_rows.filter(fromBaseRecord) : []];
    for (const status of storedStatusRows(host, 'target')) {
        if (statusItemId(status) !== STATUS_IDS.piloting || phpJsonInt(readItemField(status, 'status_deleted')) !== 0) continue;
        const target = Object.prototype.hasOwnProperty.call(status, 'status_target_id') ? readItemField(status, 'status_target_id') : self;
        const connection = upper(readItemField(status, 'status_connected_id'));
        const caster = upper(readStatusCasterId(status, self));
        if (!fromBaseUuid(target) || !fromBaseUuid(connection) || !fromBaseUuid(caster)) continue;
        const counterpart = outgoing.find(row => fromBaseRecord(row) && statusItemId(row) === STATUS_IDS.piloted
            && phpJsonInt(readItemField(row, 'status_deleted')) === 0
            && upper(readItemField(row, 'status_connected_id')) === connection
            && upper(readStatusCasterId(row)) === caster);
        const id = upper(counterpart && readItemField(counterpart, 'status_target_id'));
        if (fromBaseUuid(id) && id !== self) return id;
    }
    return null;
}

export function resolvePilotedObjectInEncounter(host: ItemFieldRow, encounter: FromBaseRow | null): string | null {
    const rows = encounter ? [...normalizePhpListish(encounter.encounter_creatures_arr),
        ...normalizePhpListish(encounter.encounter_inventories_arr)].filter(fromBaseRecord) : [];
    return resolvePilotedObjectId(host, pilotedStatusPeers(rows));
}

/** Borrow the exact held vehicle and its definition. No recursive member/encounter compilation. */
export function pilotedObjectRow(id: string, rows: readonly FromBaseRow[], definition: (id: string) => ItemFieldRow | null): FromBaseRow {
    let found = rows.find(row => upper(readInventoryField(row, 'inventory_id')
        ?? readItemField(fromBaseRecord(row.encounter_creatures) ? row.encounter_creatures : row, 'encounter_creature_id')) === upper(id));
    if (!found) for (const row of rows) {
        const nested = findInventoryRow(row, id);
        if (nested) { found = nested; break; }
    }
    if (!found) throw new PilotedObjectUnavailable(`piloted-abilities:vehicle-not-held:${id}`);
    const itemId = readInventoryField(found, 'inventory_item_id')
        ?? readItemField(fromBaseRecord(found.encounter_creatures) ? found.encounter_creatures : found, 'item_id');
    const source = typeof itemId === 'string' ? definition(itemId) : null;
    if (!fromBaseRecord(source)) throw new PilotedObjectUnavailable(`piloted-abilities:definition-not-held:${String(itemId ?? id)}`);
    return { ...source, ...found, items: source.items, item_layer_types: source.item_layer_types };
}

/** SELECT-only bases expose raw status targets for standalone as well as embedded members. */
export function fromBasePilotedObjectReaders(base: { statuses?: unknown; status_connection_rows?: unknown; status_target_references?: unknown; inventory?: unknown },
    definition: (id: string) => ItemFieldRow | null) {
    const references = fromBaseRecord(base.status_target_references) ? base.status_target_references : {};
    const inventory = Array.isArray(references.inventory) ? references.inventory.filter(fromBaseRecord).map(row => ({ inventory: row })) : [];
    const members = Array.isArray(references.encounter_creatures) ? references.encounter_creatures.filter(fromBaseRecord).map(row => ({ encounter_creatures: row })) : [];
    const ownInventory = Array.isArray(base.inventory) ? base.inventory.filter(fromBaseRecord) : [];
    const caster = fromBaseRecord(base.statuses) && Array.isArray(base.statuses.caster) ? base.statuses.caster.filter(fromBaseRecord) : [];
    const connected = Array.isArray(base.status_connection_rows) ? base.status_connection_rows.filter(fromBaseRecord) : [];
    const resolve = (host: ItemFieldRow) => resolvePilotedObjectId(host, [...caster, ...connected]);
    const query = (id: string) => pilotedObjectRow(id, [...ownInventory, ...inventory, ...members], definition);
    return { resolvePilotedObjectId: resolve, queryPilotedObject: query,
        pilotedObject: (host: ItemFieldRow): ItemFieldRow | null => {
            const id = resolve(host);
            if (!id) return null;
            try { return query(id); }
            catch (error) {
                if (error instanceof PilotedObjectUnavailable || error instanceof FromBaseInputMissing || error instanceof FromBaseStageMissing) return null;
                throw error;
            }
        } };
}

/** compute_speeds §6: the operator adopts non-teleport speeds; its own totals
 * remain on each changed slot for dismount. Inventory vehicles use their layers. */
export function applyPilotedSpeeds(speeds: FromBaseRow, object: ItemFieldRow,
    score: (row: ItemFieldRow, key: string) => number): void {
    if (!fromBaseRecord(object)) return;
    const creature = fromBaseRecord(object.computed) && fromBaseRecord(object.computed.creature) ? object.computed.creature : null;
    const computed = fromBaseRecord(creature?.speeds) ? creature.speeds : null;
    const input = { pilotTotals: {} as MutableSlots<CreatureVehicleSpeedAdoptionInput['pilotTotals']>,
        vehicleSlots: {} as MutableSlots<CreatureVehicleSpeedAdoptionInput['vehicleSlots']>,
        vehicleLegacyBase: {} as MutableSlots<CreatureVehicleSpeedAdoptionInput['vehicleLegacyBase']>,
        vehicleInventoryBase: {} as MutableSlots<CreatureVehicleSpeedAdoptionInput['vehicleInventoryBase']>,
        vehicleLayerScores: {} as MutableSlots<CreatureVehicleSpeedAdoptionInput['vehicleLayerScores']>, vehicleIsInventory: !!readInventoryField(object, 'inventory_id'),
        vehicleHasComputedSpeedsBucket: !!creature && Object.prototype.hasOwnProperty.call(creature, 'speeds') };
    const legacy = fromBaseRecord(computed?._base) ? computed._base : null;
    const inventoryBase = fromBaseRecord(object.base_speed_arr) ? object.base_speed_arr : null;
    for (const name of MOUNT_RESTRICTED_SPEEDS) {
        const previous = speeds[name], source = computed?.[name];
        const key = `speed_${name}` as const;
        input.pilotTotals[name] = phpJsonInt(fromBaseRecord(previous) ? readItemField(previous, 'total') : previous);
        input.vehicleSlots[name] = fromBaseRecord(source)
            ? { ...(readItemField(source, 'pre_mount') != null ? { pre_mount: phpJsonInt(readItemField(source, 'pre_mount')) } : {}),
                ...(readItemField(source, 'total') != null ? { total: phpJsonInt(readItemField(source, 'total')) } : {}) }
            : source == null ? null : phpJsonInt(source);
        if (legacy && readItemField(legacy, key) != null) input.vehicleLegacyBase[key] = phpJsonInt(readItemField(legacy, key));
        if (inventoryBase && readItemField(inventoryBase, key) != null) input.vehicleInventoryBase[key] = phpJsonInt(readItemField(inventoryBase, key));
        if (input.vehicleIsInventory || !input.vehicleHasComputedSpeedsBucket) input.vehicleLayerScores[key] = score(object, key);
    }
    Object.assign(speeds, adoptCreatureVehicleSpeeds(input));
}
